/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { CircularProgress } from '@mui/material'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { toast } from 'utils/toast'
import { Search } from 'components/Input/Search'
import TabsNavigation from 'components/TabsNavigation'
import Table, { TableProps, FooterWrapper, PaginationTake, PaginationPage } from 'components/Table'
import { TabsItem as TabsItemBase } from 'types/tabsItem'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { Tooltip } from 'components/Tooltip'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'

import { PlatformSlug } from 'constants/consts'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { FIND_ALL_CODES, FIND_ONE_INSTITUTIONAL_CODES } from 'queries/institutionalCodes'
import { RolesEnum } from '__generatedTypes__/globalTypes'
import * as classes from './styles'
import {
  useCodeRevealMutation,
  useRemoveCodeMutation,
  useFindAllCodesWithQueryParams,
  useFindOneInstitutionalCodesQuery,
  useFindSchoolsByPlatformQuery,
} from './hooks'
import { AddStudentModal } from './AddStudentModal'
import { formatMembership } from './helpers'

type UserCodeData = {
  id: string
  userRole: RolesEnum
  fullName: string | undefined
  email: string
  membership: string
  dateActivated: string
  code: string | null
}

type TabsItem = TabsItemBase & {
  userRole: RolesEnum
  userRoleLabel: string
}

const tabs: TabsItem[] = [
  { id: 'student-list', title: 'Student List', userRole: RolesEnum.User, userRoleLabel: 'Student' },
  { id: 'faculty-list', title: 'Faculty List', userRole: RolesEnum.Teacher, userRoleLabel: 'Faculty Member' },
]

export const EditInstitutionalCode = withPlatformCheck(() => {
  const [activeTab, setActiveTab] = useState<TabsItem>(tabs[0])
  const [isLoaded, setIsLoaded] = useState(false)
  const params = useParams<{ platform: PlatformSlug; institutionalCodeId: string }>()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [codeToDelete, setCodeToDelete] = useState<UserCodeData | null>(null)
  const [isDeletingCode, setIsDeletingCode] = useState(false)
  const [generatingCodeIds, setGeneratingCodeIds] = useState<Set<string>>(new Set())
  const { selectedPlatform } = usePlatformsAndSubjects()
  const [codeReveal] = useCodeRevealMutation()
  const [removeCode] = useRemoveCodeMutation()
  const institutionalCodeQuery = useFindOneInstitutionalCodesQuery({
    skip: !params.institutionalCodeId,
    variables: { findOneInstitutionalCodesId: params.institutionalCodeId ?? '' },
  })
  const platformId = selectedPlatform?.id ?? ''

  const schoolsByPlatformQuery = useFindSchoolsByPlatformQuery({
    skip: !platformId,
    variables: {
      platformId: platformId,
    },
  })
  const findAllCodesQuery = useFindAllCodesWithQueryParams()

  const institutionName = schoolsByPlatformQuery.data?.findSchoolListByPlatform?.schoolArray?.find(
    (school) => school.key === institutionalCodeQuery.data?.findOneInstitutionalCodes?.university
  )?.value

  useEffect(() => {
    if (params.institutionalCodeId) {
      setIsLoaded(false)
      findAllCodesQuery.setFilters({
        institutionalCodes: { id: params.institutionalCodeId },
        userRole: activeTab.userRole,
      })
    }
  }, [params.institutionalCodeId, activeTab.userRole, findAllCodesQuery.setFilters])

  useEffect(() => {
    if (findAllCodesQuery.loaded && !findAllCodesQuery.loading) {
      setIsLoaded(true)
    }
  }, [findAllCodesQuery.loaded, findAllCodesQuery.loading, findAllCodesQuery.items])

  const handleAddPartnership = () => {
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleGenerateCode = async (userId: string) => {
    try {
      setGeneratingCodeIds((prev) => new Set(prev).add(userId))
      await codeReveal({
        variables: {
          codeRevealId: userId,
        },
        refetchQueries: [FIND_ALL_CODES],
        awaitRefetchQueries: true,
      })
      toast.success('Code generated successfully!')
    } catch (error) {
      console.error('Error generating code:', error)
      toast.error('Failed to generate code. Please try again.')
    } finally {
      setGeneratingCodeIds((prev) => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  const handleDeleteCode = async () => {
    if (!codeToDelete) return
    setIsDeletingCode(true)
    try {
      await removeCode({
        variables: { id: codeToDelete.id },
        refetchQueries: [FIND_ALL_CODES, FIND_ONE_INSTITUTIONAL_CODES],
        awaitRefetchQueries: true,
      })
      toast.success(`${activeTab.userRoleLabel} deleted successfully!`)
      setCodeToDelete(null)
    } catch (error) {
      console.error(`Error deleting ${activeTab.userRoleLabel}:`, error)
      toast.error(`Failed to delete ${activeTab.userRoleLabel}. Please try again.`)
    } finally {
      setIsDeletingCode(false)
    }
  }

  const userCodes: UserCodeData[] = useMemo(
    () =>
      findAllCodesQuery.items.map((item) => ({
        id: item.id,
        userRole: item.userRole,
        code: item.code,
        dateActivated: item.activationDate,
        email: item.email,
        fullName: [item.firstName, item.lastName].filter(Boolean).join(' ') || 'N/A',
        membership: formatMembership({
          duration: Number(item.duration),
          membershipType: item.membershipType,
        }),
      })),
    [findAllCodesQuery.items]
  )

  const columns: TableProps<UserCodeData>['columns'] = useMemo(
    () => [
      {
        title: 'Name',
        renderCell: (columnItem) => <Typography variant="body2">{columnItem.fullName}</Typography>,
        flex: '1',
        orderField: 'name',
      },
      {
        title: 'Email',
        renderCell: (columnItem) => <Typography variant="body2">{columnItem.email}</Typography>,
        flex: '1',
        orderField: 'email',
      },
      {
        title: 'Membership',
        renderCell: (columnItem) => <Typography variant="body2">{columnItem.membership}</Typography>,
        flex: '1',
        orderField: 'duration',
      },
      {
        title: 'Date Activated',
        renderCell: (columnItem) => {
          const { dateActivated } = columnItem
          const formattedDate = dateActivated ? format(new Date(dateActivated), 'MMMM d, yyyy') : '-'

          return <Typography variant="body2">{formattedDate}</Typography>
        },
        flex: '1',
        orderField: 'activationDate',
      },
      {
        title: 'Code',
        renderCell: (columnItem) => {
          const { code, id } = columnItem
          const isGenerating = generatingCodeIds.has(id)

          return code ? (
            <Typography variant="body2" css={classes.codeText}>
              {code}
            </Typography>
          ) : (
            <Button
              css={classes.generateCodeButton}
              variant="contained"
              onClick={() => handleGenerateCode(id)}
              disabled={isGenerating}
            >
              {isGenerating ? <CircularProgress size={14} color="inherit" /> : 'Generate'}
            </Button>
          )
        },
        flex: '1',
        orderField: 'code',
      },
      {
        title: '',
        renderCell: (columnItem) => {
          const isActivated = !!columnItem.dateActivated

          const button = (
            <Button
              variant="outlined"
              color="red"
              size="small"
              onClick={() => setCodeToDelete(columnItem)}
              css={classes.deleteCodeButton}
              disabled={isActivated}
            >
              <TrashIcon />
            </Button>
          )

          if (isActivated) {
            return (
              <Tooltip title={`You can't delete ${activeTab.userRoleLabel} after code activation`} isNote arrow>
                {button}
              </Tooltip>
            )
          }

          return button
        },
        flex: '0 0 36px',
      },
    ],
    [handleGenerateCode, generatingCodeIds]
  )

  return (
    <div css={classes.container}>
      <Typography
        variant="title3"
        sx={{
          fontWeight: '700',
        }}
      >
        {institutionName ? `Institutional Codes - ${institutionName}` : 'Institutional Codes'}
      </Typography>

      <div css={classes.filtersSection}>
        <TabsNavigation tabs={tabs} value={activeTab} onChange={setActiveTab} />
        <Search {...findAllCodesQuery.search} />
      </div>

      <div css={classes.tableSection}>
        <Table
          columns={columns}
          items={isLoaded ? userCodes : []}
          isNotLoaded={!isLoaded}
          onOrder={findAllCodesQuery.order.onChange}
          order={findAllCodesQuery.order.value}
          wrapperMinWidth={1000}
        />

        <Button
          variant="outlined"
          color="blue"
          size="large"
          onClick={handleAddPartnership}
          css={classes.addPartnershipButton}
        >
          Add {activeTab.userRoleLabel} <AddRoundedIcon />
        </Button>
      </div>

      <FooterWrapper css={classes.footerWrapper}>
        <PaginationTake {...findAllCodesQuery.pagination} />
        <PaginationPage totalCount={findAllCodesQuery.count ?? 0} {...findAllCodesQuery.pagination} />
      </FooterWrapper>

      <AddStudentModal
        open={isModalOpen && !!params.platform}
        onClose={handleModalClose}
        institutionalCodeId={params.institutionalCodeId}
        selectedPlatform={selectedPlatform}
        userRole={activeTab.userRole}
        userRoleLabel={activeTab.userRoleLabel}
      />

      <ConfirmationModal
        open={!!codeToDelete}
        title={`Deleting ${activeTab.userRoleLabel}`}
        subtitle={`Are you sure you want to delete ${
          codeToDelete?.email ?? 'it'
        } from this partnership? This action cannot be undone.`}
        confirmText="Delete"
        closeText="Cancel"
        onConfirm={handleDeleteCode}
        onClose={() => setCodeToDelete(null)}
        isLoading={isDeletingCode}
      />
    </div>
  )
})
